import { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle, Clock, Loader2, RotateCcw, XCircle } from 'lucide-react';
import {
  APPROVAL_TONE, STATE_TONE, DEFAULT_STATE_TONE, SUBMITTER_ACTION_HINT, SUBMITTER_ACTION_STATES, SUBMITTER_NO_NOTE_HINT,
  approvalLabel, formatDate, g1Text, sourceLabel, stateLabel, typeLabel,
} from '@/lib/intakeReview';
import UseCaseDetails from '@/components/intake/UseCaseDetails';

// One intake request in a side panel: the agreed field list, the full
// submission (UseCaseDetails), the gate history, and the decision footer. Opened from the Intake requests register
// and from the Approvals inbox.
//
// `scope` picks the endpoint and so the audience: 'all' reads
// /intake-review/requests/{id} (holders of intake:read_all; the team sees
// assignees, notes and who decided, intake_reviewer sees gates and dates
// only); 'mine' reads /intake-review/mine/{id} (the caller's own request;
// the reviewer's note on changes requested, never who reviewed).
//
// The decision footer shows only when the backend says decision.can_decide,
// which is computed by the same rule POST /approvals/{id}/action enforces.

const DECISIONS = [
  { status: 'approved', label: 'Approve', icon: CheckCircle,
    className: 'bg-[#10b981] hover:bg-[#059669] text-white' },
  { status: 'changes_requested', label: 'Request changes', icon: RotateCcw,
    className: 'border-2 border-amber-400 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/10' },
  { status: 'deferred', label: 'Defer', icon: Clock,
    className: 'border-2 border-[#e3e3e3] dark:border-white/10 text-[#30302F] dark:text-white hover:bg-[#f9f5fa] dark:hover:bg-white/5' },
  { status: 'discarded', label: 'Reject', icon: XCircle,
    className: 'border-2 border-red-300 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10' },
];

const label = 'text-[11px] uppercase tracking-wide font-bold text-[#98139C]';
const value = 'text-sm text-[#30302F] dark:text-white font-light break-words';

function Field({ name, children }) {
  return (
    <div>
      <dt className={label}>{name}</dt>
      <dd className={`${value} mt-0.5`}>{children || <span className="opacity-50">Not given</span>}</dd>
    </div>
  );
}

function latestG1(approvals) {
  const g1 = (approvals || []).filter((a) => a.gate === 'G1');
  return g1.length ? g1[g1.length - 1] : null;
}

export default function RequestDrawer({ itemId, scope = 'all', onClose, onDecided }) {
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notes, setNotes] = useState('');
  const [deciding, setDeciding] = useState('');
  const [decisionError, setDecisionError] = useState('');

  const path = scope === 'mine' ? '/intake-review/mine' : '/intake-review/requests';

  const load = useCallback(async () => {
    if (!itemId) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`${path}/${encodeURIComponent(itemId)}`);
      setItem(res.data);
    } catch (err) {
      setItem(null);
      setError(err?.response?.status === 404 ? 'Request not found.' : 'Could not load this request.');
    } finally {
      setLoading(false);
    }
  }, [itemId, path]);

  useEffect(() => {
    setNotes('');
    setDecisionError('');
    load();
  }, [load]);

  const decide = async (status) => {
    const decision = item?.decision;
    if (!decision?.can_decide) return;
    setDeciding(status);
    setDecisionError('');
    try {
      await api.post(`/approvals/${encodeURIComponent(decision.approval_id)}/action`, { status, notes });
      setNotes('');
      await load();
      onDecided?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setDecisionError(typeof detail === 'string' ? detail : 'The decision was not saved.');
    } finally {
      setDeciding('');
    }
  };

  const g1 = latestG1(item?.approvals);
  const isMine = scope === 'mine';
  const waitsOnSubmitter = isMine && SUBMITTER_ACTION_STATES.includes(item?.state);
  const changeNote = waitsOnSubmitter
    ? [...(item?.approvals || [])].reverse().find((a) => a.status === 'changes_requested' && a.notes)
    : null;
  const contactName = item?.contact_name || item?.requester?.name;
  const contactEmail = item?.contact_email || item?.requester?.email;
  const decision = item?.decision;

  return (
    <Sheet open={Boolean(itemId)} onOpenChange={(open) => { if (!open) onClose?.(); }}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl lg:max-w-3xl overflow-y-auto bg-white dark:bg-[#1a1a2e] border-l border-[#e3e3e3] dark:border-white/10 text-[#30302F] dark:text-white"
        data-testid="request-drawer"
      >
        <SheetHeader className="pr-6">
          <p className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#98139C]">
            {item?.intake_ref || 'Intake request'}
          </p>
          <SheetTitle className="text-2xl font-heading text-[#30302F] dark:text-white" data-testid="request-drawer-title">
            {item?.title || (loading ? 'Loading' : 'Request')}
          </SheetTitle>
          <SheetDescription asChild>
            <div className="flex flex-wrap items-center gap-2">
              {item?.state && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATE_TONE[item.state] || DEFAULT_STATE_TONE}`}>
                  {stateLabel(item.state)}
                </span>
              )}
              {g1 && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${APPROVAL_TONE[g1.status] || ''}`}>
                  {g1Text(g1.status)}
                </span>
              )}
            </div>
          </SheetDescription>
        </SheetHeader>

        {loading && !item ? (
          <div className="flex items-center justify-center py-16 text-[#30302F]/60 dark:text-white/50">
            <Loader2 size={20} className="animate-spin" />
          </div>
        ) : error ? (
          <div className="mt-6 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-900 dark:text-red-200" role="alert">
            {error}
          </div>
        ) : item && (
          <div className="mt-6 space-y-6">
            {changeNote && (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm" data-testid="request-drawer-change-note">
                <div className="font-medium text-amber-900 dark:text-amber-200">The reviewer asked for changes</div>
                <p className="mt-1 whitespace-pre-line text-amber-900 dark:text-amber-200">{changeNote.notes}</p>
                <p className="mt-2 text-amber-800 dark:text-amber-300">{SUBMITTER_ACTION_HINT}</p>
              </div>
            )}
            {waitsOnSubmitter && !changeNote && (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-900 dark:text-amber-200" data-testid="request-drawer-needs-info">
                {SUBMITTER_NO_NOTE_HINT}
              </div>
            )}

            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="request-drawer-fields">
              <Field name="Contact person">{contactName}</Field>
              <Field name="Email">{contactEmail}</Field>
              <Field name="Business unit">{item.business_unit}</Field>
              <Field name="Type">{typeLabel(item)}</Field>
              <Field name="Urgency">{item.urgency}</Field>
              <Field name="Source">{sourceLabel(item.source)}</Field>
              <Field name="Approval">{g1Text(g1?.status)}</Field>
              <Field name="Submitted">{formatDate(item.created_at)}</Field>
              <Field name="Last updated">{formatDate(item.updated_at)}</Field>
            </dl>
            <UseCaseDetails item={item} />

            <section data-testid="request-drawer-history">
              <h3 className={label}>Approval history</h3>
              {(item.approvals || []).length === 0 ? (
                <p className={`${value} mt-1 opacity-70`}>No approvals yet.</p>
              ) : (
                <ol className="mt-2 space-y-3">
                  {item.approvals.map((a, i) => (
                    <li key={`${a.gate}-${a.created_at}-${i}`} className="border border-[#e3e3e3] dark:border-white/10 p-3">
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span className="px-2 py-0.5 text-[11px] font-semibold bg-[#f0dcf0] dark:bg-[#98139C]/20 text-[#4F0077] dark:text-[#d87bdb]">{a.gate}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${APPROVAL_TONE[a.status] || ''}`}>{approvalLabel(a.status)}</span>
                        <span className="text-xs text-[#30302F]/70 dark:text-white/60">
                          Opened {formatDate(a.created_at)}
                          {a.resolved_at && `, decided ${formatDate(a.resolved_at)}`}
                          {a.resolved_by_name && ` by ${a.resolved_by_name}`}
                        </span>
                      </div>
                      {a.assigned_names?.length > 0 && (
                        <p className="mt-1 text-xs text-[#30302F]/70 dark:text-white/60">
                          Assigned to {a.assigned_names.join(', ')}
                        </p>
                      )}
                      {a.notes && (
                        <p className="mt-2 text-sm whitespace-pre-line bg-[#faf8fb] dark:bg-white/5 p-2">{a.notes}</p>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </section>

            {decision?.can_decide && (
              <section className="border-t border-[#e3e3e3] dark:border-white/10 pt-4" data-testid="request-drawer-decision">
                <h3 className={label}>Your decision on {decision.gate}</h3>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add review notes (optional)..."
                  aria-label="Review notes"
                  rows={3}
                  className="mt-2 mb-4 rounded-none border-[#e3e3e3] dark:border-white/10 dark:bg-white/5 dark:text-white focus-visible:ring-[#98139C] text-sm"
                  data-testid="request-drawer-notes"
                />
                {decisionError && (
                  <p className="mb-3 text-sm text-red-700 dark:text-red-300" role="alert">{decisionError}</p>
                )}
                <div className="flex flex-wrap gap-3">
                  {DECISIONS.map(({ status, label: text, icon: Icon, className }) => (
                    <button
                      key={status}
                      type="button"
                      onClick={() => decide(status)}
                      disabled={Boolean(deciding)}
                      data-testid={`request-drawer-${status}`}
                      className={`rounded-full font-bold text-sm px-5 py-2.5 flex items-center gap-2 transition-colors disabled:opacity-50 ${className}`}
                    >
                      {deciding === status
                        ? <Loader2 size={16} className="animate-spin" aria-hidden="true" />
                        : <Icon size={16} aria-hidden="true" />}
                      {text}
                    </button>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
